import React, { ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Drawer } from '~/shared/components/Drawer';
import { FilterDropdownButton } from './FilterDropdownButton';
import { DrawerScrollContext } from '~/features/productList/components/VirtualizedList';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import { useRefinementDropdownScrollLock } from './useRefinementDropdownScrollLock';
import { useFilterState } from '~/features/productList/hooks/useFilterState';
import { useScrollToTopOnSearch } from '~/features/productList/hooks/useScrollToTopOnSearch';
import CloseThinIcon from '~/icons/close-thin.svg';
import styles from './styled.module.scss';

type RefinementDropdownProps = React.PropsWithChildren<{
    label?: string;
    count?: number;
    topContent?: ReactNode;
    searchValue?: string;
    onChange?: (isOpen: boolean) => void;
    headerRef?: React.Ref<HTMLDivElement>;
}>;

export const RefinementDropdown = ({
    label,
    count,
    topContent,
    searchValue = '',
    children,
    onChange,
    headerRef,
}: RefinementDropdownProps) => {
    const [isOpen, baseSetIsOpen] = useState(false);
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);

    useScrollToTopOnSearch(searchValue, scrollElement);

    const handleSetIsOpen = (open: boolean) => {
        baseSetIsOpen(open);
        onChange?.(open);
    };

    const close = () => {
        scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        handleSetIsOpen(false);
    };

    const { openDropdown, closeDropdown } = useFilterState((state) => state.actions);

    useRefinementDropdownScrollLock(isOpen);

    useEffect(() => {
        if (!isOpen) return;

        openDropdown();

        return () => closeDropdown();
    }, [isOpen, openDropdown, closeDropdown]);

    return (
        <div className={clsx(styles.refinementContainer, 'refinement-item')}>
            <FilterDropdownButton
                label={label}
                isOpen={isOpen}
                count={count}
                onClick={() => handleSetIsOpen(!isOpen)}
            >
                {label}
            </FilterDropdownButton>

            <Drawer
                ref={setScrollElement}
                open={isOpen}
                onClose={close}
                isFilterPanel
                id={`filter_${label}`}
                ariaTitle={label}
                hasVirtualizedContent
            >
                <div className={styles.drawerHeader} ref={headerRef}>
                    <div className={styles.drawerTitleRow}>
                        <span className={styles.drawerTitle}>{label}</span>
                        <ResetButton
                            className={styles.drawerCloseButton}
                            aria-label="Close"
                            onClick={close}
                        >
                            <CloseThinIcon className={styles.drawerCloseIcon} />
                        </ResetButton>
                    </div>

                    {topContent}
                </div>

                <DrawerScrollContext.Provider value={scrollElement}>
                    {children}
                </DrawerScrollContext.Provider>
            </Drawer>
        </div>
    );
};
